import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {
  PriceCalculationRequestBody,
  PriceCalculationResponseBody,
  ProductConfigurationRequestBody,
} from '@app/model/dcp-product-price.model';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {DcpKymaService} from '@app/services/dcp-kyma.service';
import {DcpCurrencyService} from '@app/services/pricing/dcp-currency.service';
import {
  CanonicalId,
  DcpBundleTemplate,
  DcpGlobalMessageService,
  DcpPriceDisplayData,
  DcpPriceItem,
  ProductDetermination,
  ProductEntitlement,
  TransactionListCta,
  TransactionType,
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Product} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {combineLatest, EMPTY, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {distinctUntilChanged, first, map, single, switchMap, tap, timeout} from 'rxjs/operators';
import {DcpProductUtilitiesService} from './dcp-product-utilities.service';

export const CTA_PRODUCT_DETERMINATION_ENDPOINT = '/cta-product-determination';
export const PARTNER_AUDIENCE = 'DCPP_017';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ProductPageDecoratorService {
  /**
   * Amount of retries to perform until we stop and fall back to contact support
   */
  public CTA_DETERMINATION_NUMBER_OF_RETRIES = 0;
  /**
   * Timeout in ms to wait until we consider the cta determination call failed
   */
  public CTA_DETERMINATION_TIMOUT = 9000;

  heroSectionCtas$: ReplaySubject<TransactionListCta[]> = new ReplaySubject<TransactionListCta[]>(1);
  priceCalculationCached: {id: string; value: PriceCalculationResponseBody};

  private latestBody: ProductConfigurationRequestBody;
  private ctaProductDetermination$: Subject<ProductDetermination> = new ReplaySubject(1);
  private ctaDeterminationPending: boolean = false;

  constructor(
    private currencyService: DcpCurrencyService,
    private http: HttpClient,
    private kymaService: DcpKymaService,
    private userFacade: UserAccountFacade,
    private countrySelectorService: CountrySelectorService,
    private productUtilitiesService: DcpProductUtilitiesService,
    private dcpGlobalMessageService: DcpGlobalMessageService
  ) {}

  getCallToActionByCode(transactionCode: string, callToActions: TransactionListCta[]): TransactionListCta {
    return (callToActions || []).find(({code}) => code === transactionCode);
  }

  getGlobalCurrencyKey(currency: string) {
    return `global-${currency}`;
  }

  getCountryCurrencyKey(currency: string) {
    return `${this.countrySelectorService.getCurrentCountry()?.isocode}-${currency}`;
  }

  getPriceCalculation(
    supplierAlternativeAid: string,
    product: Product,
    template: DcpBundleTemplate,
    activeCurrency?: string
  ): Observable<PriceCalculationResponseBody> {
    if (!supplierAlternativeAid) {
      return of(null);
    }
    return this.requestPriceCalculation(supplierAlternativeAid, product, template, activeCurrency);
  }

  /**
   * Sends a request to the price calculation of the dcp-lambda-pdp-decorator service.
   */
  requestPriceCalculation(
    supplierAlternativeAid: string,
    product: Product,
    template: DcpBundleTemplate,
    activeCurrency?: string
  ): Observable<PriceCalculationResponseBody> {
    const isCached = this.isPriceCalculationCached(supplierAlternativeAid, product, activeCurrency);
    if (isCached) {
      return of(this.priceCalculationCached.value).pipe(single());
    }
    return this.buildProductPageDecoratorRequestBody(supplierAlternativeAid, product, template).pipe(
      switchMap((body) =>
        this.http.post<PriceCalculationResponseBody>(
          this.kymaService.getUrl('pdpDecorator') + '/price-calculation',
          body
        )
      ),
      tap((priceCalculation) => this.cachePriceCalculation(supplierAlternativeAid, product, priceCalculation))
    );
  }

  isPriceCalculationCached(supplierAlternativeAid: string, product: Product, activeCurrency?: string): boolean {
    return (
      this.priceCalculationCached?.id ===
      (activeCurrency
        ? `${supplierAlternativeAid}_${product?.code}_${activeCurrency}`
        : `${supplierAlternativeAid}_${product?.code}`)
    );
  }

  cachePriceCalculation(supplierAlternativeAid: string, product: Product, value: PriceCalculationResponseBody) {
    this.priceCalculationCached = value?.items?.length
      ? {id: `${supplierAlternativeAid}_${product?.code}_${value?.items[0]?.currency}`, value}
      : {id: `${supplierAlternativeAid}_${product?.code}`, value};
  }

  /**
   * Returns the latest cta product determinations for a given Product. It can be called multiple times to get the data,
   * but the api will only be called again if the product changes.
   *
   * @param product Product we need the cta determination for
   * @param withAuth boolean to determine if we want to include the customer id in the call
   *
   * @return Observable<ProductDetermination> containing the latest cta determinations
   */
  getCtaProductDetermination(product: Product, withAuth: boolean = true): Observable<ProductDetermination> {
    this.callCtaProductDetermination(product, withAuth);
    return this.ctaProductDetermination$.asObservable();
  }

  /**
   * Calling the cta determination endpoint if the body has changed. The body change check will ignore the
   * customer id because it will be left empty for retries.
   *
   * @param product Product we need the cta determination for
   * @param withAuth boolean to determine if we want to include the customer id in the call
   * @param retry boolean to determine if we want to ignore any check and retry
   * @param retryCount number to reflect the retry iteration we are on
   */
  callCtaProductDetermination(product: Product, withAuth: boolean, retry = false, retryCount: number = 0): void {
    if (this.ctaDeterminationPending) {
      return;
    }
    this.buildProductConfigurationRequestBody(product, withAuth)
      .pipe(
        switchMap((body) => {
          if (!retry && this.compareProductConfigurationRequestBodies(this.latestBody, body)) {
            return EMPTY;
          }
          this.latestBody = body;
          this.ctaDeterminationPending = true;
          return this.http.post<ProductDetermination>(
            this.kymaService.getUrl('pdpDecorator') + CTA_PRODUCT_DETERMINATION_ENDPOINT,
            body
          );
        }),
        first(),
        timeout(this.CTA_DETERMINATION_TIMOUT),
        untilDestroyed(this)
      )
      .subscribe(
        (productDetermination) => {
          this.ctaDeterminationPending = false;
          this.ctaProductDetermination$.next(productDetermination);
          this.heroSectionCtas$.next(productDetermination.callToActions);
        },
        (e) => {
          // if clause to exclude cases where we get a response from the api but still trigger the timeout
          if (!this.ctaDeterminationPending) {
            return;
          }
          this.ctaDeterminationPending = false;
          this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging');
          // we want to prevent an infinite loop of timeouts, so we remove the customer id for retries
          if (retryCount < this.CTA_DETERMINATION_NUMBER_OF_RETRIES) {
            this.callCtaProductDetermination(product, withAuth, true, retryCount + 1);
          } else {
            this.ctaProductDetermination$.next(undefined);
          }
        }
      );
  }

  getCtaValidation(product: Product, transactionType: TransactionType): Observable<ProductDetermination> {
    return this.buildProductConfigurationRequestBody(product).pipe(
      switchMap((body) => {
        body.ctaForEntitlement = {
          id: this.productUtilitiesService.getCanonicalId(product)?.entitlementRuleId,
          cta: transactionType?.code,
        };
        return this.http.post<ProductDetermination>(this.kymaService.getUrl('pdpDecorator') + '/cta-validation', body);
      })
    );
  }

  buildDcpPriceDisplayData(): DcpPriceDisplayData {
    return {
      labels: {
        main: '',
        subs: [],
        top: '',
        tooltip: false,
      },
    };
  }

  /**
   * Creates a BundleTemplate.data.priceDisplay like Object and fills it with the priceItem and ctas.
   * This should be used for SAP products as they don't have a priceDisplay object.
   *
   * @param priceItems Items fetched from pdpDecorator/product-configuration
   */
  convertToPriceDisplay(priceItems: DcpPriceItem[]): DcpPriceDisplayData {
    let pdd: DcpPriceDisplayData = {};

    if (priceItems) {
      for (const priceItem of priceItems) {
        if (priceItem.errorCode) {
          pdd = this.buildDcpPriceDisplayData();
          this.handlePriceServiceError(priceItem.errorCode, pdd);
          return pdd;
        }
        const globalKey: string = this.getGlobalCurrencyKey(priceItem.currency);
        const countryKey: string = this.getCountryCurrencyKey(priceItem.currency);

        pdd[globalKey] = this.buildDcpPriceDisplayData();
        pdd[globalKey].labels.main = {
          currency: priceItem.currency,
          value: priceItem?.formattedValues.listPrice.replace(priceItem.currency, ''),
        };

        pdd[countryKey] = this.buildDcpPriceDisplayData();
        pdd[countryKey].labels.main = {
          currency: priceItem.currency,
          value: priceItem?.formattedValues.listPrice.replace(priceItem.currency, ''),
        };
      }
    }
    return pdd;
  }

  /**
   * Gets the callToActions from the cta response of pdpDecorator/cta-product-determination service,
   * that matches the current bundle template.
   *
   * @param cta pdpDecorator/cta-product-determination service response
   * @param template current bundle template
   */
  getEntitlementFromProductDeterminationCta(
    cta: ProductDetermination,
    template: DcpBundleTemplate
  ): ProductEntitlement {
    return this.getEntitlementFromProductDeterminationCtaByCanonicalId(
      cta,
      this.productUtilitiesService.getCanonicalId(null, template)
    );
  }

  /**
   * Gets the callToActions from the cta response of pdpDecorator/cta-product-determination service,
   * that matches the current bundle template.
   *
   * @param cta pdpDecorator/cta-product-determination service response
   * @param canonicalId canonical id of the current product
   */
  getEntitlementFromProductDeterminationCtaByCanonicalId(
    cta: ProductDetermination,
    canonicalId: CanonicalId
  ): ProductEntitlement {
    return cta?.entitlements?.find((e) => e.id === canonicalId?.entitlementRuleId);
  }

  private buildProductPageDecoratorRequestBody(
    supplierAlternativeAid: string,
    product: Product,
    template: DcpBundleTemplate,
    withAuth: boolean = true
  ): Observable<PriceCalculationRequestBody> {
    return combineLatest([this.currencyService.getActive(), this.userFacade.get()]).pipe(
      distinctUntilChanged(),
      map(([currency, user]) => {
        const priceCalculationBody: PriceCalculationRequestBody = {
          productId: supplierAlternativeAid,
          currency,
          countryCode: this.countrySelectorService.getCurrentCountry().isocode,
          technicalName: this.productUtilitiesService.getProductId(product),
        };
        // @ts-ignore
        if (withAuth && user?.scimUserId) {
          // @ts-ignore
          priceCalculationBody.customerId = user.scimUserId;
        }
        // @ts-ignore
        if (withAuth && user?.idpIds) {
          // @ts-ignore
          priceCalculationBody.idpIds = user.idpIds;
        }

        if (product?.vendor?.code) {
          priceCalculationBody.vendorId = product.vendor.code;
        }
        const commercialModel = this.productUtilitiesService.getCommercialModel(product, template);
        if (commercialModel?.code) {
          priceCalculationBody.commercialModel = commercialModel.code;
        }
        return priceCalculationBody;
      })
    );
  }

  private compareProductConfigurationRequestBodies(
    body1: ProductConfigurationRequestBody,
    body2: ProductConfigurationRequestBody
  ): boolean {
    return JSON.stringify({...body1, customerId: undefined}) === JSON.stringify({...body2, customerId: undefined});
  }

  private buildProductConfigurationRequestBody(
    product: Product,
    withAuth: boolean = true
  ): Observable<ProductConfigurationRequestBody> {
    return this.buildProductPageDecoratorRequestBody(undefined, product, undefined, withAuth).pipe(
      map((body) => {
        body.technicalName = this.productUtilitiesService.getProductId(product);
        body.productId = undefined;
        if (body.technicalName) {
          return body;
        } else {
          throw new Error('technical Name was not defined');
        }
      })
    );
  }

  /**
   * According to the error code, we set the main label of the priceDisplay object not to show anything
   * By default we set the priceNotAvailable flag to true to show a dedicated message in the UI
   * @param errorCode
   * @param pdd the display data object
   * @private
   */
  private handlePriceServiceError(errorCode: string, pdd: DcpPriceDisplayData) {
    switch (errorCode) {
      case PARTNER_AUDIENCE:
        pdd.labels.main = '';
        pdd.priceNotAvailable = false;
        break;
      default:
        pdd.priceNotAvailable = true;
        break;
    }
  }
}
